import {
  MixpanelEvent,
  NotificationVariants,
  formattedDateForSubscription,
  getDisplayNameOrNameLabel,
  toBig,
  useGlobalToasts,
  useMixpanel,
  useSocketClient,
  type MarketAccount,
} from '@talos/kyoko';
import { useCallback } from 'react';
import type { CustomRateSideInput } from './customRateValidation';

const CUSTOM_RATE_PUBLICATION_CLEANUP_TIMEOUT_MS = 10_000;

export interface BuildCustomRateRequestArgs {
  symbol: string;
  marketAccount: MarketAccount;
  bid: CustomRateSideInput;
  offer: CustomRateSideInput;
  validUntilTime?: string;
}

export interface CustomRateRequest {
  Symbol: string;
  MarketAccount: string;
  ValidUntilTime?: string;
  Bids: Array<{ Price: string; Size: string }>;
  Offers: Array<{ Price: string; Size: string }>;
}

interface CustomRatePublicationArgs {
  request: CustomRateRequest;
  event: MixpanelEvent;
  sentToastText: string;
}

export function useCustomRateApi({ displaySymbol, symbol }: { displaySymbol: string; symbol: string }) {
  const wsClient = useSocketClient();
  const mixpanel = useMixpanel();
  const { add: addToast } = useGlobalToasts();

  // Shared by publish and clear flows
  const publishMarketDataUpdate = useCallback(
    ({ request, event, sentToastText }: CustomRatePublicationArgs) => {
      if (!wsClient) {
        return false;
      }
      const cleanupPublication = wsClient.registerPublication(
        {
          type: 'UpdateMarketData',
          data: [request],
        },
        error => {
          addToast({
            text: error?.error?.msg ?? 'Failed to publish custom rate.',
            variant: NotificationVariants.Negative,
          });
        }
      );
      mixpanel.track(event);
      addToast({
        text: sentToastText,
        variant: NotificationVariants.Default,
      });

      setTimeout(() => {
        cleanupPublication();
      }, CUSTOM_RATE_PUBLICATION_CLEANUP_TIMEOUT_MS);
      return true;
    },
    [addToast, mixpanel, wsClient]
  );

  const publishCustomRate = useCallback(
    ({
      bid,
      marketAccount,
      offer,
      validUntil,
    }: {
      bid: CustomRateSideInput;
      marketAccount: MarketAccount;
      offer: CustomRateSideInput;
      validUntil: Date | null;
    }) =>
      publishMarketDataUpdate({
        request: buildCustomRateRequest({
          symbol,
          marketAccount,
          bid,
          offer,
          validUntilTime: validUntil == null ? undefined : formattedDateForSubscription(validUntil),
        }),
        event: MixpanelEvent.PublishCustomRate,
        sentToastText: `Request to publish rates for ${displaySymbol} sent.`,
      }),
    [displaySymbol, publishMarketDataUpdate, symbol]
  );

  const clearCustomRate = useCallback(
    (marketAccount: MarketAccount) =>
      publishMarketDataUpdate({
        request: buildClearCustomRateRequest({ symbol, marketAccount }),
        event: MixpanelEvent.ClearCustomRate,
        sentToastText: `Request to clear rates for ${displaySymbol} on ${getDisplayNameOrNameLabel(marketAccount)} sent.`,
      }),
    [displaySymbol, publishMarketDataUpdate, symbol]
  );

  return {
    clearCustomRate,
    publishCustomRate,
  };
}

/**
 * Builds the UpdateMarketData payload used to publish a custom rate for one market account.
 * Invalid or incomplete bid/offer inputs are encoded as empty side arrays so only complete
 * positive price and size pairs are sent.
 * When validUntilTime is omitted, the API keeps the custom rate live until it is explicitly
 * cleared by sending empty Bids and Offers arrays.
 */
export function buildCustomRateRequest({
  symbol,
  marketAccount,
  bid,
  offer,
  validUntilTime,
}: BuildCustomRateRequestArgs): CustomRateRequest {
  return {
    Symbol: symbol,
    MarketAccount: marketAccount.Name,
    ...(validUntilTime != null ? { ValidUntilTime: validUntilTime } : {}),
    Bids: buildCustomRateSideLevels(bid),
    Offers: buildCustomRateSideLevels(offer),
  };
}

/**
 * Builds the UpdateMarketData payload used to clear existing custom rates for one market account.
 * The market data API treats empty Bids and Offers arrays as a clear request for the matching
 * symbol/account, so this intentionally sends no levels on either side.
 */
export function buildClearCustomRateRequest({
  symbol,
  marketAccount,
}: Pick<BuildCustomRateRequestArgs, 'symbol' | 'marketAccount'>): CustomRateRequest {
  return {
    Symbol: symbol,
    MarketAccount: marketAccount.Name,
    Bids: [],
    Offers: [],
  };
}

/**
 * Converts one dialog side into the API level shape. A side is omitted unless both price and size
 * are positive, because the API represents missing levels as empty arrays.
 */
function buildCustomRateSideLevels(side: CustomRateSideInput) {
  return toBig(side.price)?.gt(0) === true && toBig(side.size)?.gt(0) === true
    ? [{ Price: side.price, Size: side.size }]
    : [];
}
